/**
 * schemaApi.ts
 *
 * Typed API client functions for reading a single schema (sphere type)
 * definition. Read-only — no write/publish calls belong here.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Schema } from 'types/schema';
import type { SphereSchemaAttachmentsConfig } from 'types/attachment';

/**
 * Fetch a single schema (sphere type) by id, merged with its ancestor chain.
 * Sends no `organizationId`/`identityId` — the server resolves the caller's
 * identity and tenant from the auth context.
 *
 * @param schemaClient - Schema service API client
 * @param schemaId - `schema_id` of the type to fetch
 * @param options.draft - EAINT-895 B3. When true, requests `?version=draft` —
 *   the caller's working draft, merged the same way as a published read.
 *   Omitted (the default) never requests a draft, preserving every existing
 *   read-only caller's behavior byte-for-byte.
 */
export async function getSphereType(
  schemaClient: ApiClient,
  schemaId: string,
  options?: { draft?: boolean }
): Promise<Schema> {
  const query = options?.draft ? '?version=draft' : '';
  return schemaClient.get<Schema>(`/schemas/${encodeURIComponent(schemaId)}${query}`);
}

/**
 * Fetch a sphere type's effective attachment and slot config, resolved over its inheritance chain.
 * This is the config graph-service checks before it accepts a slot upload, so a slot is offered
 * to the person exactly when the server would take it. The merged definition from
 * {@link getSphereType} does not carry `slots`.
 *
 * @param schemaClient - Schema service API client
 * @param systemName - `system_name` of the sphere type
 */
export async function getSphereSchemaConfig(
  schemaClient: ApiClient,
  systemName: string
): Promise<SphereSchemaAttachmentsConfig> {
  return schemaClient.get<SphereSchemaAttachmentsConfig>(`/schemas/config/${encodeURIComponent(systemName)}`);
}

/**
 * The two failure outcomes a fetch can map to. Never widened: a third member
 * here would necessarily leak more than the caller is allowed to know (the
 * whole point of collapsing 403 into `notFound`). The editor's four render
 * states are `SphereTypeStatus` in `useSphereType`, which adds `loading` and
 * `ready` on top of these.
 */
export type SphereTypeFetchStatus = 'notFound' | 'degraded';

/**
 * Single chokepoint mapping an HTTP status (or its absence, on a network/parse
 * failure) to a render outcome. Fail-closed: anything not explicitly `notFound`
 * maps to `degraded`, never to a state that would let a caller render a
 * definition it doesn't actually have.
 *
 * - `404` (genuinely missing) and `403` (view denied) are indistinguishable on
 *   purpose — a denied caller must not learn a schema exists.
 * - Everything else (`5xx`, network failure, malformed body, unrecognized
 *   status) is `degraded` — an authorization/availability outage, not an
 *   absence.
 */
export function mapSchemaFetchError(status: number | undefined): SphereTypeFetchStatus {
  switch (status) {
    case 404:
    case 403:
      return 'notFound';
    default:
      return 'degraded';
  }
}

export type GateCheckState = 'pass' | 'open' | 'waived';

/** One publish-readiness check (EAINT-895 B4). `sectionId` lets the footer focus the section that owns it. */
export interface GateCheck {
  id: string;
  label: string;
  state: GateCheckState;
  detail?: string;
  sectionId: string;
}

export interface SchemaGate {
  publishable: boolean;
  checks: GateCheck[];
}

/**
 * Reads the read-only publish-readiness checklist for a schema (EAINT-895 B4).
 * Gated on `view`, same as a schema read — no side effects.
 */
export async function getSchemaGate(schemaClient: ApiClient, schemaId: string): Promise<SchemaGate> {
  return schemaClient.get<SchemaGate>(`/schemas/${encodeURIComponent(schemaId)}/gate`);
}

/** One need a composed aspect's collection kind declares (EAINT-897 C4), joined with its answer. */
export interface SchemaNeed {
  /** The need's identifier; its question and noun are localized client-side from it. */
  key: string;
  at: 'type' | 'record';
  answer: string | null;
  ifMissing: 'substitute' | 'fallback' | 'refuse';
  /** The ancestor schema whose row supplied the effective answer, or null when unanswered/answered directly here. */
  inheritedFrom: string | null;
}

export interface SchemaAspectNeeds {
  aspectSystemName: string;
  needs: SchemaNeed[];
}

export interface SchemaNeedsResponse {
  aspects: SchemaAspectNeeds[];
}

/**
 * Reads the declared/answered needs for every aspect composed on a schema
 * (EAINT-897 C4) — read-only, gated on `view` like the gate.
 */
export async function getSchemaNeeds(schemaClient: ApiClient, schemaId: string): Promise<SchemaNeedsResponse> {
  return schemaClient.get<SchemaNeedsResponse>(`/schemas/${encodeURIComponent(schemaId)}/needs`);
}
